import { logger } from '@jobilla/instrumentation'
import { useCandidacyCvDestroy } from '@jobilla/nexus-sdk/lib/hooks/useCandidacyCv'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayDate } from './DisplayDate'

export interface CvFile {
	id: string
	fileName: string
	sizeInBytes: number
	uploadedAt: Date
}

export function CvCard(
	{ cv, isNew, organisationId, candidacyId, onRemoved }: {
		cv: CvFile
		isNew: boolean
		organisationId: string
		candidacyId: string
		onRemoved: () => Promise<unknown>
	},
) {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [isConfirming, setIsConfirming] = useState(false)

	const destroyCv = useCandidacyCvDestroy({
		onSuccess: () => {
			logger.track('Candidate Center: Removed CV')
			return onRemoved()
		},
		onError: error => {
			const status = error.response?.status ?? null
			logger.track('Candidate Center: CV removal failed', { data: { status } })

			if (status === 401) {
				void navigate({ to: '/', replace: true })
			}
		},
	})

	const removalError = destroyCv.isError && destroyCv.error.response?.status !== 401
		? <p role="alert" className="text-sm text-red-600">{t('profile.cv.remove.error')}</p>
		: null

	if (isConfirming) {
		return (
			<div
				role="group"
				aria-label={t('profile.cv.remove.label', { fileName: cv.fileName })}
				aria-busy={destroyCv.isPending}
				className="flex flex-col gap-2.5 rounded-lg bg-red-50 p-4 ring-1 ring-red-200 ring-inset animate-rise-in motion-reduce:animate-none"
			>
				<p className="text-md text-grey-900 [overflow-wrap:anywhere]">
					{t('profile.cv.remove.question', { fileName: cv.fileName })}
				</p>
				<p className="text-sm text-grey-700">{t('profile.cv.remove.consequence')}</p>
				{removalError}
				<div className="flex justify-end gap-2">
					<button
						type="button"
						onClick={() => {
							destroyCv.reset()
							setIsConfirming(false)
						}}
						disabled={destroyCv.isPending}
						className="min-h-11 cursor-pointer rounded-lg border border-grey-200 bg-white px-4 text-sm font-semibold text-grey-800 hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{t('profile.cv.remove.cancel')}
					</button>
					<button
						type="button"
						onClick={() =>
							destroyCv.mutate({ organisation: organisationId, candidacy: candidacyId, cv: cv.id })}
						disabled={destroyCv.isPending}
						className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-80"
					>
						{destroyCv.isPending
							? (
								<>
									<span
										aria-hidden={true}
										className="size-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
									/>
									{t('profile.cv.remove.removing')}
								</>
							)
							: t('profile.cv.remove.confirm')}
					</button>
				</div>
			</div>
		)
	}

	return (
		<div
			className={`flex items-center gap-4 rounded-lg bg-grey-50 py-3 pl-4 pr-1 ${
				isNew ? 'animate-rise-in motion-reduce:animate-none' : ''
			}`}
		>
			<span
				aria-hidden={true}
				className="flex h-12 w-10 shrink-0 items-center justify-center rounded border border-grey-200 bg-white text-xs font-semibold text-brand-600"
			>
				CV
			</span>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-md font-medium text-grey-900">{cv.fileName}</span>
				<span className="mt-0.5 block text-sm text-grey-500">
					<DisplayDate date={cv.uploadedAt} /> · {formatFileSize(cv.sizeInBytes)}
				</span>
			</span>
			<button
				type="button"
				onClick={() => setIsConfirming(true)}
				aria-label={t('profile.cv.remove.label', { fileName: cv.fileName })}
				title={t('profile.cv.remove.label', { fileName: cv.fileName })}
				className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-grey-500 transition-colors hover:bg-red-50 hover:text-red-600"
			>
				<svg
					aria-hidden={true}
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={1.8}
					strokeLinecap="round"
					strokeLinejoin="round"
					className="size-[18px]"
				>
					<path d="M3 6h18" />
					<path d="M8 6V4h8v2" />
					<path d="M19 6l-1 14H6L5 6" />
					<path d="M10 11v6M14 11v6" />
				</svg>
			</button>
		</div>
	)
}

function formatFileSize(sizeInBytes: number): string {
	if (sizeInBytes < 1024 * 1024) {
		return `${Math.max(1, Math.round(sizeInBytes / 1024))} KB`
	}

	return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`
}
